import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartConfiguration, ChartData, ChartDataset } from 'chart.js';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, distinctUntilChanged, first, tap } from 'rxjs/operators';
import { StatisticsService } from '../api/backend/services/statistics/statistics.service';
import { RegistryService, StatisticsLine } from '../api/registry.service';
import { ThingTypesService } from '../api/thing-types.service';
import { UtilsService } from '../lib/utils.service';
import { ThingType } from '../models/thingtype';
import { ThingTypesSelect } from '../models/thingtypesselect';
import { NotificationService } from '../shared/notification.service';

@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.css'],
})
export class StatisticsComponent implements OnInit {
  filters: ThingTypesSelect[] = [{ id: '1', name: 'Thing Type' }];
  dailyNewDevicesData?: ChartData;
  barChartData: ChartDataset[] = [];
  thingTypeFilter = new FormControl();

  thingTypeSubject = new BehaviorSubject<ThingType | undefined>(undefined);
  thingType$ = this.thingTypeSubject.asObservable().pipe(distinctUntilChanged());

  dailyNewDevicesLoading = false;
  numberOfDays = 7;
  barChartOptions: ChartConfiguration['options'] = {
    backgroundColor: 'rgba(217, 26, 0)',
    borderColor: 'rgba(217, 26, 0)',
    responsive: true,
  };

  constructor(
    private getThingTypesservice: ThingTypesService,
    private notif: NotificationService,
    private statisticsService: StatisticsService,
    private registryService: RegistryService,
    private router: Router,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
  ) {}

  ngOnInit(): void {
    this.loadThingTypes();

    this.route.queryParams.pipe(first()).subscribe(async (params) => {
      const thingTypeParam = params['thingType'] || '';
      this.numberOfDays = Number(params['numberOfDays']) || 7;

      await this.updateThingType(thingTypeParam);
    });

    this.thingTypeFilter.valueChanges.pipe(distinctUntilChanged()).subscribe(async (filterValue) => {
      const thingType = filterValue === 'Thing Type' ? '' : filterValue;
      await this.updateThingType(thingType);
    });
  }

  private loadThingTypes(): void {
    this.getThingTypesservice
      .getThingTypes()
      ?.pipe(
        catchError((err) => {
          this.notif.showError(err.message, err);
          return of([]);
        }),
        tap(async (thingTypes) => {
          this.filters = [{ id: '1', name: 'Thing Type' }];
          let index = 2;
          for (const type of thingTypes) {
            this.filters.push({ id: index.toString(), name: type });
            index++;
          }

          const params = this.route.snapshot.queryParams ?? {};
          const thingTypeParam = params['thingType'] || '';
          const filterValue = thingTypeParam || this.filters[0].name;
          this.numberOfDays = +params['numberOfDays'] || 7;

          const filterExists = this.filters.some((f) => f.name === filterValue);

          this.thingTypeFilter.setValue(filterExists ? filterValue : this.filters[0].name, { emitEvent: false });

          await this.updateThingType(thingTypeParam || '', false);
        }),
      )
      .subscribe();
  }

  async resetFilter(): Promise<void> {
    await this.updateThingType('');
    this.thingTypeFilter.setValue(this.filters[0].name);
  }

  async changeNumberOfDays(nbJ: number): Promise<void> {
    if (nbJ === this.numberOfDays) return;

    this.numberOfDays = nbJ;
    this.dailyNewDevicesLoading = true;

    await this.loadChartData();
    await this.updateQueryParams();
  }

  async updateThingType(thingType: string, updateChart = true): Promise<void> {
    this.thingTypeSubject.next(thingType as ThingType);

    if (updateChart) {
      this.dailyNewDevicesLoading = true;
      await this.loadChartData();
    }

    await this.updateQueryParams();
  }

  async updateQueryParams(): Promise<void> {
    const queryParams: Record<string, unknown> = {};

    if (queryParams.thingType !== this.thingTypeSubject.value) {
      queryParams.thingType = this.thingTypeSubject.value;
    }

    if (this.numberOfDays != queryParams.numberOfDays) {
      queryParams.numberOfDays = this.numberOfDays;
    }

    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  async loadChartData(): Promise<void> {
    try {
      const dailyNewDevicesData = await this.registryService.getDailyNewDevicesStats(
        this.thingTypeSubject.value as ThingType,
        this.numberOfDays,
      );

      this.dailyNewDevicesLoading = false;
      this.dailyNewDevicesData = this.buildData(dailyNewDevicesData, false, false, this.numberOfDays + 1, true);

      this.barChartData = [{ data: this.dailyNewDevicesData.datasets?.[0].data }];
    } catch (err: unknown) {
      this.notif.showError((err as Error).message, err);
      this.dailyNewDevicesLoading = false;
    }
  }

  private buildData(
    stats: StatisticsLine[],
    randomColor: boolean,
    sortValues: boolean,
    maxItems: number,
    displayZeros: boolean,
  ): ChartData {
    const ordered = sortValues ? stats.sort((a, b) => (a.count > b.count ? -1 : 1)) : stats;
    const displayed: StatisticsLine[] = [];
    let countOthers = 0;
    let hasOthers = false;
    for (const item of ordered) {
      if (displayZeros || item.count > 0) {
        if (displayed.length < maxItems - 1) {
          displayed.push(item);
        } else {
          hasOthers = true;
          countOthers += item.count;
        }
      }
    }
    if (hasOthers) {
      displayed.push({
        label: 'Others',
        count: countOthers,
      });
    }
    return {
      labels: displayed.map((_) => _.label),
      datasets: [
        {
          data: displayed.map((_) => _.count),
          backgroundColor: randomColor ? displayed.map((_) => this.utilsService.getRandomColor()) : '#d91a00',
        },
      ],
    };
  }

  startExport(): void {
    const thingType = this.thingTypeSubject.value;
    if (!thingType) return;

    this.statisticsService
      .startManualExport(thingType as ThingType)
      .pipe(
        tap(() => {
          this.notif.showSuccess('Export started. Please wait a few minutes for the export to be finished', 20000);
        }),
        catchError((err) => {
          this.notif.showError('An export is already ongoing', err);
          return of(null);
        }),
      )
      .subscribe();
  }
}
